/**
 * <SubscriptionPortalTemplate> — le corps du portail Abonnement (template, L0.1).
 *
 * L'ordre est celui du legacy (`ParentSubscriptionPortalScreen.js:1843-1868`), et il surprend :
 * « Renouvellement à venir » se rend **avant** la carte d'état, pas après. Viennent ensuite
 * « Et ensuite ? », « Gestion », « Facturation », puis « Compte » (le fuseau horaire), qui n'existe
 * pas dans le legacy.
 *
 * Une ligne de réglage n'existe que si son geste existe : un rappel absent (`onPause` non fourni,
 * par exemple) retire la ligne, et une section sans aucun geste ne se rend pas. Toutes les gardes
 * du legacy — `canPause`, `manualPausePlanned`, `!user.impersonation`, `showCancelButton` — se
 * traduisent donc en rappels absents côté view-model, et aucune règle métier n'entre dans le DS.
 *
 * Grand écran : l'état à gauche, les réglages à droite. La page n'est pas une colonne de téléphone
 * étirée ; la largeur maximale de la colonne appartient à la page, pas à ce corps.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { columnStyles } from "../../atoms/containers/columns"
import { Stack } from "../../atoms/containers/Stack"
import { SettingsSection, type SettingsSectionRow } from "../../molecules/SettingsSection"
import { SubscriptionNextStepCard } from "../../organisms/SubscriptionNextStepCard"
import { SubscriptionNoticeCard } from "../../molecules/SubscriptionNoticeCard"
import { SubscriptionStateCard } from "../../organisms/SubscriptionStateCard"
import { compact } from "../../utils/compact"
import {
    type UISubscriptionLine,
    type UISubscriptionNextStep,
    type UISubscriptionObjective,
} from "../../../ports/primary/ui/types"

export type SubscriptionPortalTemplateProps = {
    // ── « Renouvellement à venir » (rendu AVANT la carte d'état)
    /** L'avis d'échéance — absent → pas de carte. */
    renewalNotice?: { title: string; message: string } | null
    onRenewalNotice?: () => void

    // ── Carte d'état
    offerLabel?: string | null
    hoursLabel?: string | null
    statusLabel?: string | null
    endLine?: string | null
    objectives?: UISubscriptionObjective[]
    stateLines?: UISubscriptionLine[]

    // ── « Et ensuite ? »
    nextStep?: UISubscriptionNextStep | null
    onActivateNext?: () => void
    onEditNext?: () => void
    onExtend?: () => void
    extendLoading?: boolean
    onSupport?: () => void

    // ── « Gestion » — une ligne par geste disponible
    /** « Modifier mes créneaux » — la première ligne de la section. Absent = pas de ligne. */
    onChangeSlots?: () => void
    /** Une demande en cours sur cette ligne : où elle en est (« Camille n'a pas encore répondu. »). */
    changeSlotsStatus?: string | null
    onPause?: () => void
    onCancelPause?: () => void
    cancelPauseLoading?: boolean
    onExtraLesson?: () => void
    onRequestChange?: () => void
    /** « Parrainer une famille » — ouvre la page de parrainage. Absent = pas de ligne. */
    onReferral?: () => void

    // ── « Facturation »
    onCollectionDay?: () => void
    /** Jour de prélèvement en cours — affiché en valeur de la ligne. */
    paymentCollectionDay?: number | null
    /**
     * Précision à la suite du jour (« convenu avec Masteur ») : la ligne se montre même sans
     * `onCollectionDay`, et ne s'ouvre alors pas.
     */
    collectionDayNote?: string | null
    onPaymentMethod?: () => void
    onInvoices?: () => void
    onCancel?: () => void

    // ── « Compte »
    /** Ouvre l'écran « Fuseau horaire ». Absent = pas de ligne. */
    onTimezone?: () => void
    /** La ville du fuseau du compte (« Oslo ») — en valeur de la ligne. */
    timezoneCity?: string | null

    testID?: string
}

export function SubscriptionPortalTemplate(props: SubscriptionPortalTemplateProps) {
    const {
        renewalNotice,
        onRenewalNotice,
        offerLabel,
        hoursLabel,
        statusLabel,
        endLine,
        objectives,
        stateLines,
        nextStep,
        onActivateNext,
        onEditNext,
        onExtend,
        extendLoading,
        onSupport,
        onChangeSlots,
        changeSlotsStatus,
        onPause,
        onCancelPause,
        cancelPauseLoading,
        onExtraLesson,
        onRequestChange,
        onReferral,
        onCollectionDay,
        paymentCollectionDay,
        collectionDayNote,
        onPaymentMethod,
        onInvoices,
        onCancel,
        onTimezone,
        timezoneCity,
        testID,
    } = props

    const manageRows = compact<SettingsSectionRow>([
        onChangeSlots && {
            key: "change-slots",
            iconName: "calendar-alt",
            label: "Modifier mes créneaux",
            description: changeSlotsStatus ? `Demande en cours · ${changeSlotsStatus}` : undefined,
            onPress: onChangeSlots,
        },
        onPause && { key: "pause", iconName: "pause", label: "Mettre en pause", onPress: onPause },
        onCancelPause && {
            key: "cancel-pause",
            iconName: "times-circle",
            label: "Annuler la pause",
            loading: cancelPauseLoading,
            onPress: onCancelPause,
        },
        onExtraLesson && {
            key: "extra-lesson",
            iconName: "plus-circle",
            label: "Réserver un cours en plus",
            onPress: onExtraLesson,
        },
        onRequestChange && {
            key: "request-change",
            iconName: "exchange-alt",
            label: "Demander un changement",
            onPress: onRequestChange,
        },
        // Le même geste que le bandeau de l'accueil, qui a remplacé le cœur du header (28/09).
        onReferral && { key: "referral", iconName: "heart", label: "Parrainer une famille", onPress: onReferral },
    ])

    const billingRows = compact<SettingsSectionRow>([
        (onCollectionDay != null || collectionDayNote != null) && {
            key: "collection-day",
            iconName: "calendar-alt",
            label: "Date de prélèvement",
            value:
                paymentCollectionDay == null
                    ? undefined
                    : [`le ${paymentCollectionDay}`, collectionDayNote].filter(Boolean).join(", "),
            onPress: onCollectionDay,
        },
        onPaymentMethod && {
            key: "payment-method",
            iconName: "credit-card",
            label: "Moyen de paiement",
            onPress: onPaymentMethod,
        },
        onInvoices && { key: "invoices", iconName: "file-invoice", label: "Mes factures", onPress: onInvoices },
        onCancel && { key: "cancel", iconName: "times-circle", label: "Résilier", danger: true, onPress: onCancel },
    ])

    const accountRows = compact<SettingsSectionRow>([
        onTimezone && {
            key: "timezone",
            iconName: "globe",
            label: "Fuseau horaire",
            value: timezoneCity ?? undefined,
            onPress: onTimezone,
        },
    ])

    return (
        <Stack gap="4" style={styles.root} testID={testID}>
            <View style={columnStyles.columns}>
                <Stack gap="4" style={columnStyles.column}>
                    {renewalNotice != null && onRenewalNotice != null && (
                        <SubscriptionNoticeCard
                            title={renewalNotice.title}
                            message={renewalNotice.message}
                            onPress={onRenewalNotice}
                        />
                    )}

                    <SubscriptionStateCard
                        offerLabel={offerLabel}
                        hoursLabel={hoursLabel}
                        statusLabel={statusLabel}
                        endLine={endLine}
                        objectives={objectives}
                        lines={stateLines}
                    />

                    {nextStep != null && (
                        <SubscriptionNextStepCard
                            nextStep={nextStep}
                            onActivate={onActivateNext}
                            onEdit={onEditNext}
                            onExtend={onExtend}
                            extendLoading={extendLoading}
                            onSupport={onSupport}
                        />
                    )}
                </Stack>

                <Stack gap="4" style={columnStyles.column}>
                    <SettingsSection title="Gestion" rows={manageRows} />
                    <SettingsSection title="Facturation" rows={billingRows} />
                    <SettingsSection title="Compte" rows={accountRows} />
                </Stack>
            </View>
        </Stack>
    )
}

const styles = StyleSheet.create(() => ({
    // La largeur de la colonne appartient à la page (`PageTemplate maxContentWidth`).
    root: { width: "100%" },
}))
